/**
 * 下载工具函数
 * 支持通过 URL 下载文件，自动处理 HTTP 到 HTTPS 的转换
 */

/**
 * 检测是否是微信浏览器（PC 端和移动端均可识别）
 */
export const isWechatBrowser = (): boolean => {
  return /MicroMessenger/i.test(navigator.userAgent);
};

/**
 * 检测浏览器是否不支持文件下载
 * 返回 true 表示下载被禁用或不支持
 */
export const isBrowserDownloadBlocked = (): boolean => {
  const testLink = document.createElement('a');
  if (!('download' in testLink)) return true;
  if (!window.Blob) return true;
  if (!window.URL?.createObjectURL) return true;
  return false;
};

/**
 * 确保 URL 使用 HTTPS 协议
 * @param url 原始 URL
 * @returns 转换后的 HTTPS URL
 */
const ensureHttps = (url: string): string => {
  if (url.startsWith('http://')) {
    return url.replace('http://', 'https://');
  } else if (!url.startsWith('https://')) {
    // 如果 URL 没有协议，默认添加 https://
    return `https://${url}`;
  }
  return url;
};

/**
 * 通过 URL 下载文件
 * @param url 文件 URL
 * @param fileName 下载的文件名
 * @returns Promise<void>
 */
export const downloadFileByUrl = async (url: string, fileName: string): Promise<void> => {
  if (!url || url.trim() === '') {
    throw new Error('URL不存在');
  }

  // 判断 URL 协议，如果不是 https，则改为 https
  const downloadUrl = ensureHttps(url);

  // 使用 fetch 获取文件内容，创建 Blob 对象触发下载
  const response = await fetch(downloadUrl);
  if (!response.ok) {
    throw new Error('下载失败');
  }

  const blob = await response.blob();
  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(blobUrl);
};

/**
 * 下载音频文件
 * 将 HTTP 转为 HTTPS，走 fetch 网络请求，统一保存为 .mp3
 * @param url 音频文件 URL（支持 http://）
 * @param fileName 下载的文件名（含扩展名）
 * @returns Promise<void>
 */
export const downloadAudioByUrl = async (url: string, fileName: string): Promise<void> => {
  if (!url || url.trim() === '') {
    throw new Error('音频URL不存在');
  }

  // 强制转为 HTTPS，避免混合内容警告
  const downloadUrl = ensureHttps(url);

  const response = await fetch(downloadUrl);
  if (!response.ok) {
    throw new Error('音频下载失败');
  }

  const blob = await response.blob();
  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(blobUrl);
};
